<script setup lang="ts">
import ArchetypePage from '~/components/archetypes/ArchetypePage.vue';

useSeoMeta({
  title: 'ISO 9001 Records and Management Review — QikSolve',
  description:
    'Connect documented information, management review, process ownership, and continual improvement as practical parts of ISO 9001 quality management.',
});

const page = {
  hero: {
    eyebrow: 'ISO 9001 records and management review',
    title: 'Make records and management review part of quality practice',
    summary:
      'Documented information and management review work best when they help people make decisions, see performance, address risk, and improve the way work is done.',
    cta: { label: 'Discuss your quality practice', to: '/contact' },
  },
  intro: {
    title: 'Records are useful when they support decisions',
    content:
      'A quality system needs documented information, but its purpose is not to create records for their own sake. Procedures, process measures, issue records, review inputs, and actions should give teams a shared view of how work is controlled and where improvement is needed.',
    items: [
      {
        title: 'A shared source of truth',
        description: 'Relevant people can identify the approved information, current decisions, and evidence connected to the process.',
      },
      {
        title: 'Management review with context',
        description: 'Review connects performance, customer feedback, risks, actions, resources, and improvement to real priorities.',
      },
      {
        title: 'Improvement that carries through',
        description: 'Actions lead to clear ownership, proportionate follow-up, and learning that informs the next decision.',
      },
    ],
  },
  sections: [
    {
      title: 'Connect documented information to actual work',
      items: [
        {
          title: 'Keep the information usable',
          description: 'People should be able to find the information they need, understand what applies, and recognise when a change affects their work.',
        },
        {
          title: 'Make ownership visible',
          description: 'Each important procedure, process measure, review input, and action needs a clear accountable owner.',
        },
        {
          title: 'Relate records to the process',
          description: 'Records are more valuable when they show decisions, evidence, exceptions, and follow-up within the context of the operating process.',
        },
        {
          title: 'Review change as part of control',
          description: 'When work changes, the organisation should decide whether the related information, training, records, measures, or review points also need to change.',
        },
      ],
    },
    {
      title: 'Use management review to learn and decide',
      items: [
        {
          title: 'Review what matters',
          description: 'Choose meaningful inputs that show process performance, customer outcomes, risks, issues, action progress, and opportunities.',
        },
        {
          title: 'Make decisions traceable',
          description: 'Record the decisions, actions, owners, resources, and follow-up arising from review in a way the organisation can retrieve and use.',
        },
        {
          title: 'Close the feedback loop',
          description: 'The result of management review should influence daily work, priorities, quality objectives, and the next cycle of improvement.',
        },
      ],
    },
    {
      title: 'How QikSolve supports practical governance',
      items: [
        {
          title: 'Operating-model clarity',
          description: 'QikSolve helps teams examine the relationships between process ownership, records, review, evidence, and continual improvement.',
        },
        {
          title: 'Information and workflow context',
          description: 'The service context can include considering how Microsoft 365 may support structured information, access, workflows, and a usable review rhythm.',
        },
        {
          title: 'Explore the right product path',
          description: 'If you need product-specific workflows, implementation detail, or commercial guidance, the relevant product pathway can help you take the next step.',
        },
      ],
    },
  ],
  callout: {
    title: 'Good records support control; they do not replace it',
    content:
      'QikSolve is not a certification body and does not guarantee certification, audit outcomes, or compliance status. The organisation remains accountable for its own quality-system decisions, records, reviews, and oversight.',
    tone: 'boundary' as const,
  },
  relatedLinks: {
    title: 'Continue the ISO 9001 conversation',
    items: [
      {
        label: 'ISO 9001 in practice',
        description: 'Return to the corporate hub for the wider practical context of ISO 9001.',
        to: '/product/iso-9001',
      },
      {
        label: 'ISO 9001 readiness planning',
        description: 'Start with the practical operating-model questions that make readiness useful.',
        to: '/product/iso-9001-readiness-planning',
      },
      {
        label: 'Keeping procedures aligned with practice',
        description: 'Explore how controlled documentation can remain aligned with the work people actually do.',
        to: '/product/procedures-and-work-instructions-alignment',
      },
      {
        label: 'Quality dashboards',
        description: 'Explore reporting as a separate capability that can support visible management insight.',
        to: '/product/quality-dashboards',
      },
      {
        label: 'Power BI document-management metrics',
        description: 'See an existing example of reporting questions around document creation, updates, and use.',
        to: '/blog/power-bi-document-management',
      },
      {
        label: 'Map and implement your quality process',
        description: 'Explore QikSolve delivery support for process ownership, records, review, and implementation readiness.',
        to: '/services/quality-process-mapping-development-and-implementation',
      },
    ],
  },
  cta: {
    title: 'Make the next review count',
    content:
      'Bring one quality record, recurring review, or improvement decision that is not producing the clarity your team needs. We can help identify a useful next step.',
    label: 'Contact QikSolve',
    to: '/contact',
  },
};
</script>

<template>
  <ArchetypePage v-bind="page" />
</template>